import type { CapabilitySlug } from "@/content/capabilities";
import type { IndustrySlug } from "@/content/industries";

type HomeCard = {
  title: string;
  description: string;
  cta: string;
  slug: CapabilitySlug;
};

export const homePage = {
  image: "/images/brand/home-hero.webp",
  seoTitle: "General Applications | Applied AI, Search & Engineering",
  seoDescription:
    "General Applications designs and builds intelligent systems that connect reliable data, useful retrieval, dependable workflows, and clear operational oversight.",
  hero: {
    headline: "Make complicated systems **useful**.",
    copy: [
      "General Applications works with organizations facing fragmented information, unreliable search, brittle workflows, difficult-to-change platforms, or AI initiatives that need a credible path beyond the prototype.",
      "We assess, architect, build, and modernize systems where reliable information, effective software, and accountable decisions all have to work together.",
    ],
    primaryCta: "Explore Capabilities",
    secondaryCta: "Start a Conversation",
  },
  thesis: {
    eyebrow: "Operating Thesis",
    headline:
      "AI changes what software can do. **Engineering determines whether it can be trusted.**",
    copy: [
      "Software can now do more work with ambiguity, language, and unstructured information.",
      "That does not remove the need for clear boundaries, reliable retrieval, durable data contracts, measurable quality, and explicit controls around failure, review, and change.",
      "General Applications brings those disciplines together so AI can improve the work without introducing new drift, rework, or decisions nobody owns.",
    ],
    pullQuote:
      "Better systems do not merely produce more output. **They reduce uncertainty.**",
  },
  capabilities: {
    headline: "From unclear problem to **working system**.",
    copy: "Engagements can begin with an assessment, architecture plan, focused prototype, modernization effort, or direct implementation. The goal is to establish the next technically sound move and carry it far enough to produce working evidence.",
    cards: [
      {
        title: "Intelligent Systems",
        description:
          "Applied AI, grounded retrieval, evaluation, and automation with clear review and control paths.",
        cta: "Explore Intelligent Systems",
        slug: "intelligent-systems",
      },
      {
        title: "Data, Search & Knowledge",
        description:
          "Hybrid search, canonical data models, ingestion, and relevance controls that make information findable, explainable, and measurable.",
        cta: "Explore Data, Search & Knowledge",
        slug: "data-search-knowledge",
      },
      {
        title: "Automation & Operations",
        description:
          "Workflow design, system integration, exception handling, and operational tooling that make status, responsibility, and next actions easier to see.",
        cta: "Explore Automation & Operations",
        slug: "automation-operations",
      },
      {
        title: "Software Architecture & Engineering",
        description:
          "Software architecture, modernization, delivery foundations, and technical direction for systems that need to evolve safely.",
        cta: "Explore Software Architecture & Engineering",
        slug: "software-architecture-engineering",
      },
    ] satisfies HomeCard[],
  },
  industries: {
    headline:
      "The engineering principles travel. The **operating context** does not.",
    copy: [
      "Experience spans energy, agriculture, enterprise, healthcare, military, and media operations.",
    ],
    links: [
      {
        label: "Energy",
        slug: "energy",
      },
      { label: "Agriculture", slug: "agriculture" },
      { label: "Enterprise", slug: "enterprise" },
      { label: "Healthcare", slug: "healthcare" },
      { label: "Military", slug: "military" },
      { label: "Media/Entertainment", slug: "media-entertainment" },
    ] satisfies { label: string; slug: IndustrySlug }[],
  },
  philosophy: {
    headline: "Systems fail at the **seams**.",
    copy: [
      "Serious failures often begin between systems, teams, and sources of truth.",
      "Conflicting records, brittle handoffs, drifting schemas, stale search indexes, hidden exceptions, and workflows dependent on one experienced person create more risk than any missing framework.",
      "Reliable systems are built beneath the visible feature: clear ownership, durable models, explicit interfaces, measurable behavior, and change paths that do not require another partial rebuild.",
    ],
    principles: [
      "Clear models over clever abstractions",
      "Canonical data before clever dashboards",
      "Grounded retrieval before more UI chrome",
      "Bounded automation over automated chaos",
      "Operational visibility over optimistic guessing",
      "Smallest useful foundation before unnecessary complexity",
    ],
  },
  closing: {
    headline: "What system is **slowing everything else down**?",
    copy: [
      "Bring the brittle workflow, fragmented data, search problem, legacy platform, AI initiative, or stalled product.",
      "Start with the current state and the constraint that matters most. From there, we can determine the smallest next move worth building.",
    ],
    cta: "Start a Conversation",
  },
} as const;
